import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Grid } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DeviationProduct } from "@/types";
import { useGetCreateOrderValues } from "@/services/Orders";
import { useGetContractDeviationUnits } from "@/services/ContractDeviations";
import { createDeviationProduct, updateDeviationProduct } from "@/services/DeviationProducts";
import { SettingsFormContent, SettingsFormCTAs } from "@/components/pages/Settings/index";
import {
  DEVIATION_PRODUCT_UNIT_IDS,
  DeviationProductFormInput,
  DeviationProductValidationSchema,
} from "@/components/pages/Settings/validation/deviationProductValidationSchema";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import Text from "@/components/atoms/Text";
import Divider from "@/components/atoms/Divider";
import TextInput from "@/components/atoms/TextInput";
import NumberInput from "@/components/atoms/NumberInput";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import DeviationProductTable from "@/components/organisms/SettingsTables/DeviationProductTable";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getApiError } from "@/utils/apiErrors";

interface DeviationProductSettingsProps {
  companyId: number | undefined;
  /** Only admins can create or change deviation products. */
  isAdmin: boolean;
}

/**
 * The company's deviation products, e.g. residual waste or gravel: parts of a delivery a receiving inspection sorts
 * out. They're never on a contract, so each always costs the company's own price.
 */
export default function DeviationProductSettings({ companyId, isAdmin }: DeviationProductSettingsProps) {
  const [selectedProduct, setSelectedProduct] = useState<DeviationProduct | null>(null);

  return (
    <>
      <DeviationProductForm
        companyId={companyId}
        isAdmin={isAdmin}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
      />
      <DeviationProductTable isAdmin={isAdmin} companyId={companyId} setSelectedProduct={setSelectedProduct} />
    </>
  );
}

interface DeviationProductFormProps extends DeviationProductSettingsProps {
  selectedProduct: DeviationProduct | null;
  setSelectedProduct: React.Dispatch<React.SetStateAction<DeviationProduct | null>>;
}

const DeviationProductForm = ({
  companyId,
  isAdmin,
  selectedProduct,
  setSelectedProduct,
}: DeviationProductFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = ["deviationProducts", companyId];

  const { data: createOrderValues } = useGetCreateOrderValues();
  const { data: contractDeviationUnits } = useGetContractDeviationUnits();
  const units = contractDeviationUnits?.filter((unit) => DEVIATION_PRODUCT_UNIT_IDS.includes(String(unit.id)));

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<DeviationProductFormInput>({
    resolver: zodResolver(DeviationProductValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      companyId: companyId as number,
      name: selectedProduct?.name || "",
      price: selectedProduct?.price || 0,
      currency: selectedProduct?.currency || "NOK",
      priceUnit: selectedProduct?.priceUnit.toString() || "6",
      description: selectedProduct?.description || "",
    },
  });

  // 244: the backend rejects negative prices and units other than kg or tonnes, which the form doesn't offer.
  const saveErrorMessage = (error: unknown) =>
    getApiError(error).errorCode === 244 ? t("validation:priceNegative") : t("notifications:generic.createError");

  // The list only changes once the backend has saved, so a refused save never shows in it.
  const { mutateAsync: createProduct, isPending: isPendingCreate } = useMutation({
    mutationFn: (data: DeviationProductFormInput) => createDeviationProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:generic.createSuccess"),
          "deviation-product-created-success"
        )
      );
    },
    onError: (err) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), saveErrorMessage(err), "deviation-product-created-error")
      );
    },
  });

  const { mutateAsync: updateProduct, isPending: isPendingUpdate } = useMutation({
    mutationFn: (data: DeviationProductFormInput) => updateDeviationProduct(selectedProduct?.id as number, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:generic.createSuccess"),
          "deviation-product-updated-success"
        )
      );
    },
    onError: (err) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), saveErrorMessage(err), "deviation-product-updated-error")
      );
    },
  });

  const onSubmit = async (data: DeviationProductFormInput) => {
    if (selectedProduct) {
      await updateProduct(data);
    } else {
      await createProduct(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <Grid.Col span={12}>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:deviationSettings.addProduct")}
            </Text>
            <Text size="sm" color="secondary">
              {t("settings:deviationSettings.productsDescription")}
            </Text>
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 10 }}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="product-name"
                  data-testid="deviation-product-name-input"
                  label={t("common:name")}
                  value={field.value || ""}
                  disabled={!isAdmin}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Group gap={15} align="flex-start">
              <Controller
                control={control}
                name="price"
                render={({ field, fieldState }) => (
                  <NumberInput
                    {...field}
                    id="product-price"
                    value={field.value || 0}
                    label={t("common:price")}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    hideControls={true}
                    min={0}
                    allowNegative={false}
                    disabled={!isAdmin}
                  />
                )}
              />
              <Controller
                control={control}
                name="currency"
                render={({ field, fieldState }) => {
                  const currencies = createOrderValues?.currencies.map((currency) => currency.value.toUpperCase());
                  return (
                    <Select
                      {...field}
                      id="product-currency"
                      label={t("common:currency")}
                      data={transformDataToSelectOption(currencies || [], "value", "value", t, undefined, true)}
                      value={field.value || ""}
                      disabled={!isAdmin}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="priceUnit"
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    id="product-priceUnit"
                    label={t("common:unit")}
                    data={transformDataToSelectOption(units || [], "id", "code", t, "enumerables:paymentIntervals")}
                    value={String(field.value)}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    disabled={!isAdmin}
                  />
                )}
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="product-description"
                  data-testid="deviation-product-description-input"
                  label={t("common:description")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  disabled={!isAdmin}
                />
              )}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA
          disabled={!isAdmin || !isValid}
          id="product-cancel"
          size="lg"
          intent={"secondary"}
          type="button"
          onClick={() => setSelectedProduct(null)}
        >
          {t("actions:cancel")}
        </CTA>
        <CTA
          disabled={!isAdmin || !isValid}
          id="product-save"
          data-testid="deviation-product-submit-btn"
          size="lg"
          type="submit"
          loading={isPendingCreate || isPendingUpdate}
        >
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};
